<template>
  <Tile
    :name="productTypeName"
    :display-status="false"
    clickable
    :tiled-background="false"
    :image="props.image || productTypeImage"
    :image-overlay-icon="props.imageOverlayIcon"
    :badges="allBadges"
    :is-selectable="props.isSelectable"
    :selected="props.selected"
    :always-show-checkbox="props.alwaysShowCheckbox"
    :context-menu-options="props.contextMenuOptions"
    :selection-disabled="props.selectionDisabled"
    @toggle-selection="emit('toggleSelection')"
    @on-click="onClick"
    @context-menu-click="(option) => emit('contextMenuClick', option)"
  >
    <template #sideinfo>
      <div class="card-footer">
        <p v-if="productType">
          {{
            $t('PUBLISHING.DETAIL.PRODUCTS.ADVANCED.BY_BRAND', {
              brand: productType.brandName,
            })
          }}
        </p>
        <ColorsIndicator
          v-if="defaultAppearance"
          :appearances="allAppearances"
          :max-colors="props.maxColors"
        />
      </div>
    </template>
  </Tile>
</template>

<script setup lang="ts">
import Tile from 'commons/tileView/Tile.vue';
import { computed } from 'vue';
import { useCompositionStore } from 'commons/stores/composition';
import {
  CompositionSpecificCategoriesTree,
  CompositionSpecificCategoryProductType,
} from 'commons/composition/compositionCategoryHelper';
import compositionHelper from 'commons/composition/compositionHelper';
import { useStore } from 'vuex';
import ColorsIndicator from './ColorsIndicator.vue';
import { getProductType } from '@spreadgroup/common-partner-lib/composition';
import type { TileBadge } from 'src/app/components/badge/badgeTypes';

export interface AssortmentCardProps {
  productTypeId: number;
  viewId?: number;
  image?: string;
  appearanceIds?: number[];
  maxColors?: number;
  imageOverlayIcon?: string;
  isSelectable?: boolean;
  selected?: boolean;
  alwaysShowCheckbox?: boolean;
  contextMenuOptions?: { key: string; name: string }[];
  defaultAppearanceId?: number;
  /** A click on the tile toggles the selection instead of opening the product for editing. */
  selectOnClick?: boolean;
  /** Shown but not pickable — e.g. a selection limit has already been reached. */
  selectionDisabled?: boolean;
  /**
   * Extra badges for this card's product type, on top of its own feature labels. Labels are expected
   * translated.
   *
   * Define it once in the caller, **not inline in the template**: a fresh function identity on every
   * render would make this card re-render for nothing.
   */
  getExtraBadges?: (
    productType: CompositionSpecificCategoryProductType,
  ) => TileBadge[];
}

const props = defineProps<AssortmentCardProps>();

const emit = defineEmits<{
  edit: [];
  toggleSelection: [];
  contextMenuClick: [{ key: string; name: string }];
}>();

const onClick = () => {
  if (props.selectionDisabled) return;
  emit(props.selectOnClick ? 'toggleSelection' : 'edit');
};

const store = useStore();
const compositionStore = useCompositionStore();
const compositionCategories = computed(
  () => compositionStore.categories as CompositionSpecificCategoriesTree,
);

const productType = computed(
  (): CompositionSpecificCategoryProductType | undefined => {
    if (!compositionCategories.value) return undefined;
    return getProductType(compositionCategories.value, props.productTypeId) as
      | CompositionSpecificCategoryProductType
      | undefined;
  },
);

const labels = computed(
  () =>
    productType.value?.labels
      ?.map(
        (label) =>
          store.getters['publishingCore/featureLabels'].find(
            (featureLabel: { id: string }) => featureLabel.id === label.id,
          )?.name,
      )
      .filter((label) => !!label) || [],
);

/**
 * `Tile` renders at most two badges, so where the extra ones go decides whether they show at all.
 * Second place both guarantees it and matches the designs, which pair it with the leading feature
 * label ("New | Top pick").
 */
const allBadges = computed(() => {
  const featureBadges: TileBadge[] = labels.value.map((label) => ({
    label,
    type: 'light',
  }));

  const extraBadges =
    productType.value && props.getExtraBadges
      ? props.getExtraBadges(productType.value)
      : [];
  if (!extraBadges.length) return featureBadges;

  return [
    ...featureBadges.slice(0, 1),
    ...extraBadges,
    ...featureBadges.slice(1),
  ];
});

const productTypeName = computed(
  () =>
    store.state.publishingCore.translations.productTypes[props.productTypeId]
      ?.name,
);
const appearanceNames = computed(
  () => store.state.publishingCore.translations.appearances,
);
const appearanceColors = computed(() => store.state.publishingCore.appearances);

const productTypeImage = computed(() => {
  if (!productType.value || !defaultAppearance.value) return undefined;
  return `${compositionHelper.getProductTypeImage(
    productType.value.id,
    props.viewId || productType.value.defaultViewId,
    defaultAppearance.value,
  )}?width=250`;
});

const defaultAppearance = computed(() => {
  return (
    props.defaultAppearanceId ||
    props.appearanceIds?.[0] ||
    productType.value?.defaultAppearance
  );
});

const allAppearances = computed(() => {
  return (props.appearanceIds || productType.value?.appearanceIds || [])
    .map((appearanceId) => ({
      id: appearanceId,
      name: appearanceNames.value[appearanceId]?.name || '',
      color: appearanceColors.value.find(
        (appearance) => appearance.id === appearanceId,
      )?.colors,
    }))
    .filter((appearance) => appearance.color && appearance.color.length > 0);
});
</script>

<style lang="scss" scoped>
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/links';

.sellables {
  .heading {
    margin-bottom: 4px;
  }

  & > h1 {
    margin: 0 0 8px 0;
  }

  p {
    margin: 0;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    margin-top: 40px;

    h2 {
      margin: 0;
    }

    img {
      width: 200px;
      height: auto;
      margin-bottom: 8px;
    }
  }
}

ul {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin: 0;
  padding: 0;

  li {
    list-style: none;
  }
}

.card-footer {
  width: 100%;
}
</style>
